import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { CustomInput, CustomModal, CustomFormField } from "@shared/components";
import { useOnClickOutside } from "../../hooks";
import { isValidEmail } from "../../utils";

/**
 * Impersonation Badge â€” small indicator on avatar
 */
export const ImpersonationBadge = () => (
  <Chip
    icon={<VisibilityOutlinedIcon sx={{ fontSize: "0.6rem !important", color: "#fff !important" }} />}
    label="LIVE"
    size="small"
    sx={{
      position: "absolute",
      bottom: -4,
      right: -6,
      height: 16,
      fontSize: "0.5rem",
      fontWeight: 700,
      letterSpacing: "0.5px",
      bgcolor: "#f97316",
      color: "#fff",
      border: "1.5px solid #fff",
      pointerEvents: "none",
      "& .MuiChip-label": { px: "4px" },
      "& .MuiChip-icon": { ml: "4px" },
    }}
  />
);

/**
 * Impersonation Banner â€” full-width bar shown when actively impersonating
 */
export const ImpersonationBanner = ({ impersonatedEmail, onStop, loading }) => (
  <Box
    className="w-full flex items-center justify-between px-4"
    sx={{ bgcolor: "#f97316", color: "#fff", py: "6px", zIndex: 1100, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
  >
    <Box className="flex items-center gap-2 min-w-0">
      <VisibilityOutlinedIcon sx={{ fontSize: "0.9rem", flexShrink: 0 }} />
      <Typography
        component="span"
        className="font-bold tracking-wide uppercase"
        sx={{ fontSize: "0.7rem", flexShrink: 0 }}
      >
        Impersonating
      </Typography>
      <Typography
        component="span"
        className="truncate"
        sx={{ fontSize: "0.75rem", opacity: 0.92, minWidth: 0 }}
      >
        {impersonatedEmail}
      </Typography>
    </Box>

    <Button
      size="small"
      variant="outlined"
      onClick={onStop}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={10} sx={{ color: "#fff" }} /> : null}
      sx={{
        ml: 2,
        flexShrink: 0,
        color: "#fff",
        borderColor: "rgba(255,255,255,0.6)",
        fontSize: "0.7rem",
        fontWeight: 700,
        borderRadius: "999px",
        py: "2px",
        px: "12px",
        minWidth: "unset",
        textTransform: "none",
        "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.15)" },
        "&.Mui-disabled": { color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.3)" },
      }}
    >
      {loading ? "Stoppingâ€¦" : "âœ• Stop"}
    </Button>
  </Box>
);

/**
 * Profile Dropdown Menu
 */
export const ProfileMenu = ({
  open,
  onClose,
  onOpenImpersonate,
  onStopImpersonation,
  impersonating,
  loading,
  canImpersonate,
}) => {
  const menuRef = useRef(null);
  useOnClickOutside(menuRef, onClose, open);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Paper
      ref={menuRef}
      elevation={6}
      className="absolute right-0"
      sx={{
        top: "calc(100% + 6px)",
        minWidth: 200,
        borderRadius: "8px",
        border: "1px solid #e8e8e8",
        overflow: "hidden",
        zIndex: 50,
        py: 0.5,
      }}
      role="menu"
      aria-label="Profile menu"
    >
      <MenuList disablePadding sx={{ py: 0 }}>
        {/* My Profile */}
        <MenuItem
          component={Link}
          to="/my-profile"
          onClick={onClose}
          role="menuitem"
          sx={{
            py: "9px",
            px: 2,
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "#2b2b2b",
            textDecoration: "none",
            gap: "10px",
            minHeight: "unset",
            "&:hover": { bgcolor: "#f4f4f4" },
          }}
        >
          <AccountCircleOutlinedIcon sx={{ fontSize: "1.15rem", color: "#5a5a5a" }} />
          My Profile
        </MenuItem>

        {/* Login as user */}
        {canImpersonate && !impersonating && (
          <>
            <Divider sx={{ my: 0 }} />
            <MenuItem
              onClick={onOpenImpersonate}
              sx={{
                py: "9px",
                px: 2,
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "#3901FF",
                gap: "10px",
                minHeight: "unset",
                "&:hover": { bgcolor: "#ede9ff" },
              }}
            >
              <ManageAccountsOutlinedIcon sx={{ fontSize: "1.15rem", color: "#3901FF" }} />
              Login as user
            </MenuItem>
          </>
        )}

        {/* Stop impersonation */}
        {impersonating && (
          <>
            <Divider sx={{ my: 0 }} />
            <MenuItem
              onClick={onStopImpersonation}
              disabled={loading}
              sx={{
                py: "9px",
                px: 2,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "#e60000",
                gap: "10px",
                minHeight: "unset",
                "&:hover": { bgcolor: "#fff0f0" },
                "&.Mui-disabled": { opacity: 0.5 },
              }}
            >
              {loading
                ? <CircularProgress size={14} sx={{ color: "#e60000", flexShrink: 0 }} />
                : <LogoutOutlinedIcon sx={{ fontSize: "1.15rem", color: "#e60000" }} />
              }
              {loading ? "Stopping…" : "Stop impersonation"}
            </MenuItem>
          </>
        )}
      </MenuList>
    </Paper>
  );
};

ProfileMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenImpersonate: PropTypes.func.isRequired,
  onStopImpersonation: PropTypes.func.isRequired,
  impersonating: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  canImpersonate: PropTypes.bool.isRequired,
};

ImpersonationBanner.propTypes = {
  impersonatedEmail: PropTypes.string.isRequired,
  onStop: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

/**
 * Impersonate Modal
 */
export const ImpersonateModal = ({ open, onClose, onSubmit, loading, error, setError }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setEmailError("");
    setError("");
  }, [open, setError]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setEmailError("");
    setError("");
    if (!email.trim()) { setEmailError("Email is required."); return; }
    if (!isValidEmail(email)) { setEmailError("Enter a valid email address."); return; }
    const success = await onSubmit(email.trim());
    if (success) onClose();
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
    setError("");
  };

  const canSubmit = isValidEmail(email) && !loading;

  return (
    <CustomModal
      isOpen={open}
      onClose={onClose}
      title="Login as User"
      size="md"
      footer={
        <Box className="flex gap-3 justify-end">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderColor: "#e8e8e8",
              color: "#5a5a5a",
              borderRadius: "8px",
              "&:hover": { borderColor: "#d4d4d4", bgcolor: "#f9f9f9" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              loading
                ? <CircularProgress size={14} sx={{ color: "rgba(255,255,255,0.8)" }} />
                : <ManageAccountsOutlinedIcon sx={{ fontSize: "1rem" }} />
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderRadius: "8px",
              bgcolor: "#3901FF",
              "&:hover": { bgcolor: "#2d00cc" },
              "&.Mui-disabled": { bgcolor: "#d4d4d4", color: "#fff" },
            }}
          >
            {loading ? "Logging inâ€¦" : "Login as user"}
          </Button>
        </Box>
      }
    >
      <form onSubmit={handleSubmit} autoComplete="off" noValidate>
        <CustomFormField
          label="Email address"
          required
          error={emailError || error}
          helperText={emailError || error}
        >
          <CustomInput
            id="impersonate-email"
            type="email"
            placeholder="Enter email to impersonate"
            value={email}
            onChange={handleChange}
            disabled={loading}
            error={!!(emailError || error)}
            autoFocus
            fullWidth
          />
        </CustomFormField>
      </form>
    </CustomModal>
  );
};

ImpersonateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  setError: PropTypes.func.isRequired,
};


